import { TranslationService } from "../../services/TranslationService";
import TabPosition from "../shared/TabPosition";
import CurrencyConfiguration from "./Configuration/CurrencyConfiguration";
import DashboardConfiguration from "./Configuration/DashboardPanelConfiguration";
import EmailTemplateConfiguration from "./Configuration/EmailTemplateConfiguration";
import OtherConfiguration from "./Configuration/OtherConfiguration";
import VerticalMenu from "../shared/components/VerticalMenu";
import TransactionType from "./Configuration/TransactionType";
import ActivityType from "./Configuration/ActivityType";
import InvoiceStatus from "./Configuration/InvoiceStatus";
import DataCompanyConfiguration from "./Configuration/DataCompanyConfiguration";
import TagsConfiguration from "./Configuration/TagsConfiguration";
import SMSConfiguration from "./Configuration/SMSConfiguration";
import { ConfigurationProvider } from "./ConfigurationContext";
import WhatsappTemplateConfiguration from "./Configuration/WhatsappTemplateConfiguration";
import CompanyService from "../../services/CompanyService";
import {
  ClientFieldConfiguration,
  InvoiceFieldConfiguration,
  PaymentFieldConfiguration
} from "./Configuration/FieldConfiguration/Fields/EntityFieldConfiguration";
import ImportLog from "./Configuration/ImportLog";
import AssistantConfiguration from "./Configuration/AssistantConfiguration";
import AIAgentUsage from "./Configuration/aiBilling/AIAgentUsage";
import AIAgentUsageDetail from "./Configuration/aiBilling/AIAgentUsageDetail";
import ApiRequestLog from "./Configuration/ApiRequestLog";
import ProcessLog from "./Configuration/ProcessLog";
import { addIf } from "../../utils/Utils";
import UserList from "../user/List";
import ImportTemplateConfiguration from "./Configuration/ImportTemplate/ImportTemplateConfiguration";
import { Helmet } from "react-helmet-async";
import RoleList from "../roles/List";
import PaymentGatewayConfiguration from "./Configuration/PaymentGatewayConfiguration";
import PaymentGatewayTransactionLog from "./Configuration/PaymentGatewayTransactionLog";
import { useHistory } from "react-router-dom";
import { AIAgentType } from "../../entities/company/AIBilling/AIBilling";

const aiBillingEnabled: boolean = true;

const CompanyConfiguration: React.FC = () => {
  const { translate } = TranslationService;
  const history = useHistory();

  if (!CompanyService.canEditCompanySettings()) {
    history.push(`/${TranslationService.currentLanguage}/company`);
  }

  let tabs = [
    ...addIf(CompanyService.canDoAny(["editcompany", "setcompany"]), {
      title: translate.Company,
      component: DataCompanyConfiguration
    }),
    ...addIf(CompanyService.canDoAny(["viewuser", "edituser"]), {
      title: translate.Users,
      component: UserList
    }),
    ...addIf(CompanyService.canDoAny(["viewrole", "editrole"]), {
      title: translate.Roles,
      component: RoleList
    }),
    ...addIf(CompanyService.canDo("editcurrency"), {
      title: translate.Currencies,
      component: CurrencyConfiguration
    }),
    ...addIf(
      CompanyService.canDoAny([
        "setfieldsperson",
        "setfieldsio",
        "setfieldstransaction"
      ]),
      {
        title: translate.Fields,
        hasItems: true,
        items: [
          ...addIf(CompanyService.canDo("setfieldsperson"), {
            title: translate.Client,
            component: ClientFieldConfiguration,
            hash: "ClientFields"
          }),
          ...addIf(CompanyService.canDo("setfieldsio"), {
            title: translate.Invoice,
            component: InvoiceFieldConfiguration,
            hash: "InvoiceFields"
          }),
          ...addIf(CompanyService.canDo("setfieldstransaction"), {
            title: translate.Payment,
            component: PaymentFieldConfiguration,
            hash: "PaymentFields"
          })
        ]
      }
    ),
    ...addIf(
      CompanyService.canDoAny([
        "editiostatus",
        "editactivitytype",
        "newtag",
        "edittag",
        "deletetag",
        "edittransactiontype"
      ]),
      {
        title: translate.Tipificaciones,
        hasItems: true,
        items: [
          ...addIf(CompanyService.canDoAny(["editiostatus"]), {
            title: translate.EstadosFacturas,
            component: InvoiceStatus
          }),
          ...addIf(CompanyService.canDoAny(["editactivitytype"]), {
            title: translate.ActivitiesTypes,
            component: ActivityType
          }),
          ...addIf(
            CompanyService.canDoAny(["newtag", "edittag", "deletetag"]),
            { title: translate.EtiquetasGestion, component: TagsConfiguration }
          ),
          ...addIf(CompanyService.canDoAny(["edittransactiontype"]), {
            title: translate.PaymentTypes,
            component: TransactionType
          })
        ]
      }
    ),
    ...addIf(
      CompanyService.canDoAny(["viewimportlog", "setimport"]) ||
        (CompanyService.getCompanyToken()?.collectionapi === "1" &&
          CompanyService.canDo("importdata")),
      {
        title: translate.Importaciones,
        hasItems: true,
        items: [
          ...addIf(CompanyService.canDo("setimport"), {
            title: translate.Configuration,
            component: ImportTemplateConfiguration
          }),
          ...addIf(CompanyService.canDo("viewimportlog"), {
            title: translate.ImportLog,
            component: ImportLog
          }),
          ...addIf(
            CompanyService.getCompanyToken()?.collectionapi === "1" &&
              CompanyService.canDo("importdata"),
            {
              title: translate.ApiRequestLog,
              component: ApiRequestLog
            }
          )
        ]
      }
    ),
    ...addIf(
      CompanyService.canDoAny([
        "settemplatesms",
        "settemplateemail",
        "settemplatewhatsapp"
      ]),
      {
        title: translate.Templates,
        hasItems: true,
        items: [
          ...addIf(CompanyService.canDo("settemplateemail"), {
            title: translate.EmailTemplates,
            component: EmailTemplateConfiguration
          }),
          ...addIf(
            CompanyService.canShowWhatsApp() &&
              CompanyService.canDo("settemplatewhatsapp"),
            {
              title: translate.WhatsAppTemplates,
              component: WhatsappTemplateConfiguration
            }
          ),
          ...addIf(CompanyService.canDo("settemplatesms"), {
            title: translate.SMSTemplates,
            component: SMSConfiguration
          })
        ]
      }
    ),
    ...addIf(
      CompanyService.canShowPaymentProvider() &&
        CompanyService.canDo("setpayment"),
      {
        title: translate.PaymentGateway,
        hasItems: true,
        items: [
          {
            title: translate.Configuration,
            component: PaymentGatewayConfiguration,
            hash: "ConfiguracionPagos"
          },
          {
            title: translate.PaymentGatewayTransactionLog,
            component: PaymentGatewayTransactionLog
          }
        ]
      }
    ),
    { title: translate.EstatusProcesos, component: ProcessLog },
    ...addIf(CompanyService.canDo("editdashboard"), {
      title: "Dashboard",
      component: DashboardConfiguration
    }),
    ...addIf(CompanyService.canDo("setcompanyothersettings"), {
      title: translate.OtherSettings,
      component: () => (
        <ConfigurationProvider>
          <OtherConfiguration />
        </ConfigurationProvider>
      )
    }),
    ...addIf(
      (CompanyService.isBackOfficeUser() ||
        CompanyService.canDo("setaiassistant")) &&
        CompanyService.canShowAgentAI(AIAgentType.Categorize), //Ajustar luego con más configuraciones de agentes
      aiBillingEnabled
        ? {
            title: translate.AIAssistant,
            hasItems: true,
            items: [
              {
                title: translate.AIAgentUsage,
                component: AIAgentUsage,
                hash: "AIAgentUsage"
              },
              {
                title: translate.AIPeriodUsage,
                component: AIAgentUsageDetail,
                hash: "AIAgentUsageDetail",
                hidden: true
              },
              ...addIf(CompanyService.canShowAgentAI(AIAgentType.Categorize), {
                title: translate.CategorizeEmails,
                component: AssistantConfiguration,
                hash: "AIAssistantConfiguration"
              })
            ]
          }
        : {
            title: translate.AIAssistant,
            component: AssistantConfiguration
          }
    )
  ];

  return (
    <TabPosition module="Configuration">
      <Helmet>
        <title>Intiza</title>
      </Helmet>
      <div className="main-layout-container">
        <div className="main-panel configuration">
          <VerticalMenu
            items={tabs}
            title={TranslationService.translate.CompanySettings}
          />
        </div>
      </div>
    </TabPosition>
  );
};

export default CompanyConfiguration;
